import { memo, useEffect, useMemo, useState } from 'react'
import { IoIosArrowBack, IoIosArrowForward, IoIosClose } from 'react-icons/io'
import { useShallow } from 'zustand/react/shallow'
import { useClaimReward } from '../hooks/queries/use-claim-reward'
import { useDoCheckin } from '../hooks/queries/use-do-checkin'
import { useGetLevels } from '../hooks/queries/use-get-levels'
import { useGetLevelsRewards } from '../hooks/queries/use-get-levels-rewards'
import { useGetMonthRewards } from '../hooks/queries/use-get-month-rewards'
import { useGetMonthTasks } from '../hooks/queries/use-get-month-tasks'
import { useGetOrCreateMonth } from '../hooks/queries/use-get-or-create-month'
import { useGetTasksDefaults } from '../hooks/queries/use-get-tasks-defaults'
import { closeWebview } from '../helpers/format'
import { useGlobalStore } from '../store/global-store'
import { LevelCarouselHeader } from './level-carousel-header'
import { RewardCard } from './reward-card'
import { TaskActionsCard } from './task-actions-card'
import { TaskCard } from './task-card'

// Recompensas em moedas por nível escondidas (decisão do dono, 23/10/2026): o
// resgate nunca funcionou (nenhuma recompensa com received=1 e sem resolver de
// claimLiveCheckinMonthLevelReward no worker LIVES). Não prometer o que não paga.
const SHOW_LEVEL_REWARDS = false

export const CheckInModal = memo(() => {
  const { liveUserId, myUserId } = useGlobalStore(
    useShallow((s) => ({ liveUserId: s.liveUserId, myUserId: s.myUserId })),
  )
  const hostUserId = liveUserId ?? myUserId

  const {
    month,
    isLoading: loadingMonth,
    error: monthError,
    apiError: monthApiError,
  } = useGetOrCreateMonth(hostUserId)
  const { tasksDefaults, isLoading: loadingDefaults } = useGetTasksDefaults()
  const { levels, isLoading: loadingLevels } = useGetLevels()
  const { rewards: catalogRewards, isLoading: loadingRewards } = useGetLevelsRewards()
  const { tasks: monthTasks, isLoading: loadingMonthTasks } = useGetMonthTasks(month?.id)
  const { monthRewards, isLoading: loadingMonthRewards } = useGetMonthRewards(month?.id)
  const { claim, isClaiming } = useClaimReward()
  const { doCheckin, isCheckingIn, result: checkinResult } = useDoCheckin()

  // Loading inicial: aguarda catálogo + mês + tarefas do mês.
  // Recompensas do mês são opcionais (podem ser 0).
  const isInitialLoading =
    !!hostUserId &&
    !monthError &&
    !monthApiError &&
    (loadingMonth ||
      loadingDefaults ||
      loadingLevels ||
      loadingRewards ||
      (!!month?.id && (loadingMonthTasks || loadingMonthRewards)))

  // Níveis em ordem ascendente
  const orderedLevels = useMemo(
    () => [...levels].sort((a, b) => a.level - b.level),
    [levels],
  )

  // Defaults agrupados por nível
  const defaultsByLevelId = useMemo(() => {
    const map = new Map<number, typeof tasksDefaults>()
    for (const d of tasksDefaults) {
      if (d.liveCheckinLevelId == null) continue
      const arr = map.get(d.liveCheckinLevelId) ?? []
      arr.push(d)
      map.set(d.liveCheckinLevelId, arr)
    }
    return map
  }, [tasksDefaults])

  // Recompensas agrupadas por nível
  const rewardsByLevelId = useMemo(() => {
    const map = new Map<number, typeof catalogRewards>()
    for (const r of catalogRewards) {
      const arr = map.get(r.liveCheckinLevelId) ?? []
      arr.push(r)
      map.set(r.liveCheckinLevelId, arr)
    }
    return map
  }, [catalogRewards])

  // MonthTask por task_default_id
  const monthTaskByDefaultId = useMemo(() => {
    const map = new Map<number, (typeof monthTasks)[number]>()
    for (const t of monthTasks) map.set(t.liveCheckinTaskDefaultId, t)
    return map
  }, [monthTasks])

  // MonthReward por level_reward_id
  const monthRewardsByLevelRewardId = useMemo(() => {
    const map = new Map<number, (typeof monthRewards)[number]>()
    for (const mr of monthRewards) map.set(mr.liveCheckinLevelRewardId, mr)
    return map
  }, [monthRewards])

  // ------------------------------------------------------------
  // Carrossel de níveis
  // ------------------------------------------------------------
  const [levelIndex, setLevelIndex] = useState(0)
  const [taskIndex, setTaskIndex] = useState(0)
  const safeIndex = Math.min(
    levelIndex,
    Math.max(0, orderedLevels.length - 1),
  )
  const currentLevel = orderedLevels[safeIndex] ?? null

  // Reset task carousel ao trocar de nível
  useEffect(() => {
    setTaskIndex(0)
  }, [safeIndex])

  // Quando o mês carrega, pula automaticamente para o nível "atual" do user
  // (primeiro incompleto). Só executa uma vez por mudança de mês.
  const monthIdSignal = month?.id ?? 0
  useEffect(() => {
    if (!month || !orderedLevels.length) return
    const targetLevelNumber = (month.levelCurrent ?? 0) + 1
    const idx = orderedLevels.findIndex((l) => l.level === targetLevelNumber)
    if (idx >= 0) setLevelIndex(idx)
    else if (month.levelCurrent) {
      // Já completou tudo — mostra o último
      setLevelIndex(orderedLevels.length - 1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthIdSignal, orderedLevels.length])

  // Tarefas e recompensas do nível atual selecionado
  const currentLevelDefaults = currentLevel
    ? (defaultsByLevelId.get(currentLevel.id) ?? [])
    : []
  const sortedDefaults = useMemo(
    () =>
      [...currentLevelDefaults].sort(
        (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
      ),
    [currentLevelDefaults],
  )

  const currentLevelRewards = currentLevel
    ? (rewardsByLevelId.get(currentLevel.id) ?? [])
    : []
  const sortedRewards = useMemo(
    () =>
      [...currentLevelRewards].sort(
        (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
      ),
    [currentLevelRewards],
  )

  const tasksCompleted = sortedDefaults.filter(
    (d) => monthTaskByDefaultId.get(d.id)?.completed,
  ).length
  const tasksTotal = sortedDefaults.length

  const isMyOwnCheckin = hostUserId === myUserId

  return (
    <div
      className="relative min-h-screen w-screen overflow-y-auto rounded-t-3xl text-white"
      style={{
        background:
          'radial-gradient(120% 80% at 50% 0%, #2d1b69 0%, #1a0f3d 45%, #0a0820 100%)',
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 30%, white 0.5px, transparent 1.2px),' +
            'radial-gradient(circle at 70% 60%, white 0.6px, transparent 1.4px),' +
            'radial-gradient(circle at 40% 80%, white 0.4px, transparent 1px),' +
            'radial-gradient(circle at 85% 25%, white 0.5px, transparent 1.2px)',
          backgroundSize: '200px 200px, 300px 300px, 150px 150px, 250px 250px',
        }}
      />

      <button
        onClick={closeWebview}
        className="absolute top-4 right-4 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-white/15 backdrop-blur-md transition hover:bg-white/25 active:scale-95"
        aria-label="close"
      >
        <IoIosClose className="h-6 w-6 text-white" />
      </button>

      <div className="relative z-10 mx-auto flex max-w-md flex-col gap-4 px-4 pb-10 pt-14">
        {/* Loading inicial: ocupa só o container (não a tela toda) */}
        {isInitialLoading ? (
          <LoadingPanel />
        ) : (
          <>
        {/* Header carrossel de níveis */}
        {!hostUserId ? (
          <ErrorEmpty message="Sem usuário na URL (?liveUserId= ou ?myUserId=)" />
        ) : monthError ? (
          <ErrorEmpty message={`Falha ao conectar com a API: ${monthError.message}`} />
        ) : monthApiError ? (
          <ErrorEmpty message={`API: ${monthApiError}`} />
        ) : !month ? (
          <ErrorEmpty message="Sem dados de check-in para este usuário." />
        ) : orderedLevels.length === 0 ? (
          <ErrorEmpty message="Nenhum nível cadastrado." />
        ) : (
          <LevelCarouselHeader
            month={month}
            level={currentLevel}
            levelIndex={safeIndex}
            totalLevels={orderedLevels.length}
            tasksCompleted={tasksCompleted}
            tasksTotal={tasksTotal}
            onPrev={() => setLevelIndex((i) => Math.max(0, i - 1))}
            onNext={() =>
              setLevelIndex((i) =>
                Math.min(orderedLevels.length - 1, i + 1),
              )
            }
          />
        )}

        {/* Card de ações rápidas — todos os tipos de tarefa clicáveis (check-in
            na sala, compartilhar via RN, abrir jogos via RN). */}
        {hostUserId && month && (
          <TaskActionsCard
            tasks={tasksDefaults}
            onCheckin={() => hostUserId && doCheckin(hostUserId)}
            isCheckingIn={isCheckingIn}
            checkedInToday={
              checkinResult?.alreadyToday === true ||
              checkinResult?.status === 'success'
            }
          />
        )}

        {/* Tarefas DESSE nível — carrossel com setas */}
        {currentLevel && (
          <section className="rounded-2xl border border-white/15 bg-white/5 p-3 backdrop-blur-md">
            <header className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold tracking-wide text-white/90">
                Tarefas do nível
              </h3>
              <span className="text-[11px] text-white/60">
                {tasksCompleted}/{tasksTotal}
              </span>
            </header>

            {tasksTotal === 0 ? (
              <p className="text-center text-xs text-white/60 py-2">
                Sem tarefas para esse nível.
              </p>
            ) : (
              (() => {
                const safeTaskIdx = Math.min(
                  taskIndex,
                  Math.max(0, tasksTotal - 1),
                )
                const currentDef = sortedDefaults[safeTaskIdx]
                const monthTask = currentDef
                  ? monthTaskByDefaultId.get(currentDef.id)
                  : undefined
                const placeholder = currentDef
                  ? (monthTask ?? {
                      id: -currentDef.id,
                      liveCheckinMonthId: month?.id ?? 0,
                      liveCheckinTaskDefaultId: currentDef.id,
                      userId: hostUserId ?? 0,
                      liveCheckinTaskType: currentDef.liveCheckinTaskType,
                      targetValue: currentDef.targetValue,
                      quantityCompleted: 0,
                      pointReward: currentDef.pointReward,
                      pointReceived: 0,
                      completed: false,
                      dateCompleted: null,
                    })
                  : null
                const isFirstTask = safeTaskIdx <= 0
                const isLastTask = safeTaskIdx >= tasksTotal - 1
                return (
                  <>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          setTaskIndex((i) => Math.max(0, i - 1))
                        }
                        disabled={isFirstTask}
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 backdrop-blur-md transition active:scale-95 ${
                          isFirstTask ? 'opacity-30' : 'hover:bg-white/20'
                        }`}
                        aria-label="prev task"
                      >
                        <IoIosArrowBack className="h-5 w-5 text-white" />
                      </button>

                      <div className="min-w-0 flex-1">
                        <TaskCard task={placeholder} def={currentDef} />
                      </div>

                      <button
                        onClick={() =>
                          setTaskIndex((i) =>
                            Math.min(tasksTotal - 1, i + 1),
                          )
                        }
                        disabled={isLastTask}
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 backdrop-blur-md transition active:scale-95 ${
                          isLastTask ? 'opacity-30' : 'hover:bg-white/20'
                        }`}
                        aria-label="next task"
                      >
                        <IoIosArrowForward className="h-5 w-5 text-white" />
                      </button>
                    </div>

                    {/* dots de paginação */}
                    <div className="mt-3 flex flex-wrap items-center justify-center gap-1.5">
                      {sortedDefaults.map((d, i) => {
                        const t = monthTaskByDefaultId.get(d.id)
                        const completed = !!t?.completed
                        const active = i === safeTaskIdx
                        return (
                          <button
                            key={d.id}
                            onClick={() => setTaskIndex(i)}
                            aria-label={`task ${i + 1}`}
                            className={`h-1.5 rounded-full transition-all ${
                              active
                                ? 'w-6 bg-amber-300'
                                : completed
                                  ? 'w-2.5 bg-emerald-400'
                                  : 'w-2.5 bg-white/30'
                            }`}
                          />
                        )
                      })}
                    </div>

                    <div className="mt-2 text-center text-[11px] text-white/60">
                      Tarefa {safeTaskIdx + 1} de {tasksTotal}
                    </div>
                  </>
                )
              })()
            )}
          </section>
        )}

        {/* Recompensas DESSE nível */}
        {SHOW_LEVEL_REWARDS && currentLevel && (
          <section className="rounded-2xl border border-white/15 bg-white/5 p-3 backdrop-blur-md">
            <header className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold tracking-wide text-white/90">
                Recompensas do nível
              </h3>
              <span className="text-[11px] text-white/60">
                {sortedRewards.length}
              </span>
            </header>

            {sortedRewards.length === 0 ? (
              <p className="text-center text-xs text-white/60 py-2">
                Sem recompensas para esse nível.
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {sortedRewards.map((r) => (
                  <RewardCard
                    key={r.id}
                    reward={r}
                    level={currentLevel}
                    monthReward={monthRewardsByLevelRewardId.get(r.id)}
                    onClaim={isMyOwnCheckin ? claim : undefined}
                    isClaiming={isClaiming}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {SHOW_LEVEL_REWARDS && !isMyOwnCheckin && (
          <p className="rounded-lg bg-white/5 px-3 py-2 text-center text-[11px] text-white/60">
            Você está vendo o progresso do host. Só o próprio host pode resgatar.
          </p>
        )}

        {/* Debug pequeno: contagens recebidas */}
        <p className="text-center text-[10px] text-white/30">
          host #{hostUserId ?? '—'} · defs {tasksDefaults.length} · lvls {levels.length} · mtTasks {monthTasks.length}
        </p>
          </>
        )}
      </div>
    </div>
  )
})

CheckInModal.displayName = 'CheckInModal'

const LoadingPanel = memo(() => (
  <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur-md">
    <div className="relative h-10 w-10">
      <div className="absolute inset-0 rounded-full border-2 border-white/15" />
      <div className="absolute inset-0 animate-spin rounded-full border-2 border-transparent border-t-amber-300" />
    </div>
    <p className="text-xs text-white/70">Carregando check-in…</p>
  </div>
))
LoadingPanel.displayName = 'LoadingPanel'

const ErrorEmpty = memo(({ message }: { message: string }) => (
  <div className="rounded-2xl border border-red-400/40 bg-red-500/10 p-4 text-center text-sm text-red-100">
    {message}
  </div>
))
ErrorEmpty.displayName = 'ErrorEmpty'
